import React, { useState, useEffect } from 'react';
import { Plus, Search, X, Edit2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000/api/v1';

export default function DocenteTareas() {
  const currentYear = new Date().getFullYear();
  const [selectedGestion, setSelectedGestion] = useState(currentYear);
  const [tipoGestion, setTipoGestion] = useState(''); // 'matriculacion', 'preadmision', 'admision'
  const [searchTerm, setSearchTerm] = useState('');
  const [examenes, setExamenes] = useState([]);

  // Asignaciones aprobadas del docente (de asignaciones_horario con estado_direccion='aprobado')
  const [asignacionesAprobadas, setAsignacionesAprobadas] = useState([]);

  const [showModal, setShowModal] = useState(false);
  const [editingExamen, setEditingExamen] = useState(null);
  const [loading, setLoading] = useState(false);
  const [paralelos, setParalelos] = useState([]);

  const [formData, setFormData] = useState({
    tipoGestion: 'matriculacion',
    id_carrera: '',
    id_mencion: '',
    id_asignatura: '',
    id_paralelo: '',
    sala: '',
    tipo_examen: '',
    fecha: '',
    hora: ''
  });

  const getHeaders = () => ({
    'Authorization': `Bearer ${localStorage.getItem('token')}`,
    'Content-Type': 'application/json'
  });

  useEffect(() => {
    loadAsignacionesAprobadas();
    loadExamenes();
  }, [selectedGestion, tipoGestion]);

  // Cargar asignaciones para el modal (cuando cambia tipoGestion en el modal)
  useEffect(() => {
    if (formData.tipoGestion) {
      loadAsignacionesAprobadasModal(formData.tipoGestion);
    } else {
      setAsignacionesAprobadas([]);
    }
  }, [formData.tipoGestion]);

  // Cargar asignaciones aprobadas del docente para la lista principal (opcional, para filtrar o validar)
  const loadAsignacionesAprobadas = async () => {
    try {
      if (!tipoGestion) {
        setAsignacionesAprobadas([]);
        return;
      }
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const response = await fetch(`${API_URL}/docente/asignaturas?id_usuario=${user.id_usuario || ''}&tipo=${tipoGestion}`, { headers: getHeaders() });
      const data = await response.json();
      setAsignacionesAprobadas(data.asignaturas || []);
    } catch (error) {
      console.error('Error loading asignaciones aprobadas:', error);
    }
  };

  const loadAsignacionesAprobadasModal = async (tipo) => {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const response = await fetch(`${API_URL}/docente/asignaturas?id_usuario=${user.id_usuario || ''}&tipo=${tipo}`, { headers: getHeaders() });
      const data = await response.json();
      setAsignacionesAprobadas(data.asignaturas || []);
    } catch (error) {
      console.error('Error loading asignaciones aprobadas modal:', error);
    }
  };

  const loadExamenes = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/docente/tareas?gestion=${selectedGestion}`, { headers: getHeaders() });
      const data = await response.json();
      setExamenes(data.tareas || []);
    } catch (error) {
      console.error('Error loading examenes:', error);
      setExamenes([]);
    } finally {
      setLoading(false);
    }
  };

  const loadParalelos = async (id_asignatura) => {
    try {
      const response = await fetch(`${API_URL}/docente/paralelos/${id_asignatura}`, { headers: getHeaders() });
      const data = await response.json();
      setParalelos(data.paralelos || []);
    } catch (error) {
      console.error('Error loading paralelos:', error);
      setParalelos([]);
    }
  };

  // ===== Listas derivadas de las asignaciones aprobadas (cascada) =====

  // Carreras únicas del docente
  // Carreras únicas del docente (incluyendo "Sin asignar" si hay asignaciones sin carrera)
  const carreras = [];
  const seenC = new Set();
  let hasNullCarrera = false;
  asignacionesAprobadas.forEach(a => {
    if (!a.id_carrera) {
      hasNullCarrera = true;
      return;
    }
    const id = String(a.id_carrera);
    if (!seenC.has(id)) {
      seenC.add(id);
      carreras.push({ id_carrera: a.id_carrera, nombre: a.carrera_nombre || a.carrera });
    }
  });
  if (hasNullCarrera) {
    carreras.unshift({ id_carrera: '__none__', nombre: '(Sin carrera asignada)' });
  }

  // Menciones filtradas por carrera seleccionada
  const menciones = [];
  const seenM = new Set();
  let hasNullMencion = false;
  asignacionesAprobadas
    .filter(a => {
      if (!formData.id_carrera) return true;
      if (formData.id_carrera === '__none__') return !a.id_carrera;
      return String(a.id_carrera) === String(formData.id_carrera);
    })
    .forEach(a => {
      if (!a.id_mencion) {
        hasNullMencion = true;
        return;
      }
      const id = String(a.id_mencion);
      if (!seenM.has(id)) {
        seenM.add(id);
        menciones.push({ id_mencion: a.id_mencion, nombre: a.mencion_nombre || a.mencion });
      }
    });
  if (hasNullMencion) {
    menciones.unshift({ id_mencion: '__none__', nombre: '(Sin mención asignada)' });
  }

  // Asignaturas filtradas por carrera + mencion
  const asignaturas = [];
  const seenA = new Set();
  asignacionesAprobadas
    .filter(a => {
      if (formData.id_carrera) {
        if (formData.id_carrera === '__none__') { if (a.id_carrera) return false; }
        else if (String(a.id_carrera) !== String(formData.id_carrera)) return false;
      }
      if (formData.id_mencion) {
        if (formData.id_mencion === '__none__') { if (a.id_mencion) return false; }
        else if (String(a.id_mencion) !== String(formData.id_mencion)) return false;
      }
      return true;
    })
    .forEach(a => {
      const id = String(a.id_asignatura);
      if (!seenA.has(id)) {
        seenA.add(id);
        asignaturas.push(a);
      }
    });
  const handleChange = (e) => {
    const { name, value } = e.target;
    const newForm = { ...formData, [name]: value };

    // Cascada: limpiar downstream al cambiar upstream
    if (name === 'tipoGestion') {
      newForm.id_carrera = '';
      newForm.id_mencion = '';
      newForm.id_asignatura = '';
      newForm.id_paralelo = '';
      newForm.sala = '';
      setParalelos([]);
    } else if (name === 'id_carrera') {
      newForm.id_mencion = '';
      newForm.id_asignatura = '';
      newForm.id_paralelo = '';
      setParalelos([]);
    } else if (name === 'id_mencion') {
      newForm.id_asignatura = '';
      newForm.id_paralelo = '';
      setParalelos([]);
    } else if (name === 'id_asignatura') {
      newForm.id_paralelo = '';
      newForm.paralelo = '';
      if (value) {
        loadParalelos(value);
      } else {
        setParalelos([]);
      }
    } else if (name === 'id_paralelo') {
      // Buscar el nombre del paralelo seleccionado para guardarlo en la columna 'paralelo'
      const selectedP = paralelos.find(p => String(p.id_paralelo) === String(value) || p.nombre === value);
      newForm.paralelo = selectedP ? selectedP.nombre : value;
    }

    setFormData(newForm);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const url = editingExamen
        ? `${API_URL}/docente/tareas/${editingExamen.id_tarea}`
        : `${API_URL}/docente/tareas`;

      const method = editingExamen ? 'PUT' : 'POST';

      const sanitize = (v) => (v && v !== '__none__') ? v : null;

      const response = await fetch(url, {
        method,
        headers: getHeaders(),
        body: JSON.stringify({
          id_asignatura: sanitize(formData.id_asignatura),
          id_carrera: sanitize(formData.id_carrera),
          id_mencion: sanitize(formData.id_mencion),
          id_paralelo: sanitize(formData.id_paralelo),
          sala: formData.sala || null,
          paralelo: formData.paralelo || null,
          tipo_examen: formData.tipo_examen || null,
          titulo: 'Evaluación',
          fecha_presentacion: formData.fecha || null,
          hora: formData.hora || null,
          tipoGestion: formData.tipoGestion || 'licenciatura'
        })
      });

      const data = await response.json();

      if (response.ok) {
        alert(data.message || 'Examen guardado');
        setShowModal(false);
        resetForm();
        await loadExamenes();
      } else {
        alert(data.message || 'Error al guardar examen');
      }
    } catch (error) {
      console.error('Error saving examen:', error);
      alert('Error al guardar examen');
    }
  };

  const handleEdit = (examen) => {
    setEditingExamen(examen);
    // Inferir tipoGestion del código de asignatura o carrera
    const isPreadmision = examen.asignatura_codigo?.startsWith('CPA');
    const isAdmision = examen.asignatura_codigo?.startsWith('PAA');
    const tipo = isPreadmision ? 'preadmision' : (isAdmision ? 'admision' : 'matriculacion');

    setFormData({
      tipoGestion: tipo,
      id_carrera: examen.id_carrera || '',
      id_mencion: examen.id_mencion || '',
      id_asignatura: examen.id_asignatura || '',
      id_paralelo: examen.id_paralelo || '',
      sala: examen.sala || '',
      paralelo: examen.paralelo || '',
      tipo_examen: examen.tipo_examen || '',
      fecha: examen.fecha_presentacion ? examen.fecha_presentacion.split('T')[0] : '',
      hora: examen.hora || ''
    });
    if (examen.id_asignatura) {
      loadParalelos(examen.id_asignatura);
    }
    setShowModal(true);
  };

  const resetForm = () => {
    setEditingExamen(null);
    setFormData({
      tipoGestion: 'matriculacion',
      id_carrera: '',
      id_mencion: '',
      id_asignatura: '',
      id_paralelo: '',
      sala: '',
      paralelo: '',
      tipo_examen: '',
      fecha: '',
      hora: ''
    });
  };

  const filteredExamenes = examenes.filter(e => {
    const matchesSearch = searchTerm === '' ||
      e.asignatura_nombre?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      e.carrera_nombre?.toLowerCase().includes(searchTerm.toLowerCase());

    const isPreadmision = e.asignatura_codigo?.startsWith('CPA');
    const isAdmision = e.asignatura_codigo?.startsWith('PAA');
    const tipo = isPreadmision ? 'preadmision' : (isAdmision ? 'admision' : 'matriculacion');

    const matchesTipo = !tipoGestion || tipo === tipoGestion;

    return matchesSearch && matchesTipo;
  });

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-6">
      <div className="mb-6">
            <h1 className="text-xl font-bold text-gray-800">Docente &gt; Evaluaciones</h1>
          </div>
      <div className="max-w-7xl mx-auto">
        {/* Header con controles */}
        <div className="flex items-center gap-3 mb-4">
          <button
            onClick={() => {
              resetForm();
              setShowModal(true);
            }}
            className="flex items-center gap-2 px-4 py-2 bg-blue-900 text-white rounded-lg hover:bg-blue-800"
            title="Añadir Evaluación"
          >
            <Plus size={20} />
            Añadir Evaluación
          </button>

          <div className="relative flex-1 max-w-xs">
            <input
              type="text"
              placeholder="Buscar..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-4 pr-10 py-2 border border-gray-300 rounded-full"
            />
            <Search className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
          </div>

          <select
            value={selectedGestion}
            onChange={(e) => setSelectedGestion(parseInt(e.target.value))}
            className="px-4 py-2 border border-gray-300 rounded-full bg-white"
          >
            {[2024, 2025, 2026, 2027].map(g => (
              <option key={g} value={g}>Gestión {g}</option>
            ))}
          </select>
        </div>

        {/* Tabla */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          {loading ? (
            <div className="p-8 text-center text-gray-500">Cargando...</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="px-3 py-3 text-center font-semibold border">No</th>
                    <th className="px-3 py-3 text-center font-semibold border">Carrera</th>
                    <th className="px-3 py-3 text-center font-semibold border">Mención</th>
                    <th className="px-3 py-3 text-center font-semibold border">Asignatura</th>
                    <th className="px-3 py-3 text-center font-semibold border">Paralelo</th>
                    <th className="px-3 py-3 text-center font-semibold border">Sala</th>
                    <th className="px-3 py-3 text-center font-semibold border">Tipo Examen</th>
                    <th className="px-3 py-3 text-center font-semibold border">Fecha</th>
                    <th className="px-3 py-3 text-center font-semibold border">Hora</th>
                    <th className="px-3 py-3 text-center font-semibold border">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredExamenes.length === 0 ? (
                    <tr>
                      <td colSpan="10" className="p-8 text-center text-gray-500">
                        No hay exámenes registrados
                      </td>
                    </tr>
                  ) : (
                    filteredExamenes.map((examen, idx) => (
                      <tr key={examen.id_tarea || idx} className="border-b hover:bg-gray-50">
                        <td className="p-3 text-sm text-center border-r">{idx + 1}</td>
                        <td className="p-3 text-sm text-center border-r">{examen.carrera_nombre || '-'}</td>
                        <td className="p-3 text-sm text-center border-r">{examen.mencion_nombre || '-'}</td>
                        <td className="p-3 text-sm text-center border-r">{examen.asignatura_nombre || '-'}</td>
                        <td className="p-3 text-sm text-center border-r">{examen.paralelo || '-'}</td>
                        <td className="p-3 text-sm text-center border-r">{examen.sala || '-'}</td>
                        <td className="p-3 text-sm text-center border-r">{examen.tipo_examen || '-'}</td>
                        <td className="p-3 text-sm text-center border-r">
                          {examen.fecha_presentacion ? new Date(examen.fecha_presentacion).toLocaleDateString() : '-'}
                        </td>
                        <td className="p-3 text-sm text-center border-r">{examen.hora || '-'}</td>
                        <td className="p-3 text-center">
                          <button
                            onClick={() => handleEdit(examen)}
                            className="p-1 text-blue-600 hover:bg-blue-50 rounded"
                            title="Editar"
                          >
                            <Edit2 size={18} />
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Modal Añadir Examen */}
        {showModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg w-full max-w-lg">
              <div className="flex items-center justify-between p-4 border-b">
                <h3 className="text-xl font-bold text-blue-900">{editingExamen ? 'Editar Evaluación' : 'Añadir Evaluación'}</h3>
                <button onClick={() => setShowModal(false)} className="text-gray-500 hover:text-gray-700">
                  <X size={24} />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="p-6">
                <div className="grid grid-cols-2 gap-4">
                  {/* Tipo de Gestión */}
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Tipo de Gestión</label>
                    <div className="w-full px-3 py-2 border border-blue-200 bg-blue-50 rounded-lg font-semibold text-blue-900">
                      Licenciatura
                    </div>
                  </div>

                  {formData.tipoGestion === 'matriculacion' ? (
                    <>
                      {/* Carrera - SOLO las del docente aprobadas */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Carrera *</label>
                        <select
                          name="id_carrera"
                          value={formData.id_carrera}
                          onChange={handleChange}
                          required={formData.tipoGestion === 'matriculacion'}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="">Seleccione Carrera</option>
                          {carreras.map(c => (
                            <option key={c.id_carrera} value={c.id_carrera}>{c.nombre}</option>
                          ))}
                        </select>
                      </div>

                      {/* Mención - SOLO las del docente aprobadas, filtradas por carrera */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Mención</label>
                        <select
                          name="id_mencion"
                          value={formData.id_mencion}
                          onChange={handleChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="">Seleccione Mención</option>
                          {menciones.map(m => (
                            <option key={m.id_mencion} value={m.id_mencion}>{m.nombre}</option>
                          ))}
                        </select>
                      </div>
                    </>
                  ) : null}

                  {/* Asignatura - SOLO las del docente aprobadas, filtradas por carrera + mencion */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Asignatura</label>
                    <select
                      name="id_asignatura"
                      value={formData.id_asignatura}
                      onChange={handleChange}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Seleccionar</option>
                      {asignaturas.map(a => (
                        <option key={a.id_asignatura} value={a.id_asignatura}>
                          {a.nombre} ({a.codigo})
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Paralelo - ahora un select dependiente */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Paralelo *</label>
                    <select
                      name="id_paralelo"
                      value={formData.id_paralelo || ''}
                      onChange={handleChange}
                      required
                      disabled={!formData.id_asignatura}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                    >
                      <option value="">Seleccione Paralelo</option>
                      {paralelos.map((p) => (
                        <option key={p.id_paralelo} value={p.id_paralelo || p.nombre}>{p.nombre}</option>
                      ))}
                      {/* Fallback si no hay paralelos registrados pero la asignatura está seleccionada */}
                      {paralelos.length === 0 && formData.id_asignatura && (
                        <option value="A">Paralelo A</option>
                      )}
                    </select>
                  </div>

                  {/* Sala - dropdown original 1-20 */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Sala</label>
                    <select
                      name="sala"
                      value={formData.sala || ''}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Seleccione Sala</option>
                      {Array.from({ length: 20 }, (_, i) => i + 1).map(n => (
                        <option key={n} value={n}>Sala {n}</option>
                      ))}
                    </select>
                  </div>

                  {/* Tipo Examen */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Tipo Examen</label>
                    <select
                      name="tipo_examen"
                      value={formData.tipo_examen}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value=""></option>
                      <option value="Teórico">Teórico</option>
                      <option value="Práctico">Práctico</option>
                      <option value="Teórico/Práctico">Teórico/Práctico</option>
                    </select>
                  </div>

                  {/* Fecha */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Fecha</label>
                    <div className="relative">
                      <input
                        type="date"
                        name="fecha"
                        value={formData.fecha}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>

                  {/* Hora */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Hora</label>
                    <div className="relative">
                      <input
                        type="time"
                        name="hora"
                        value={formData.hora}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                </div>

                <div className="flex justify-end mt-6">
                  <button
                    type="submit"
                    className="px-6 py-2 bg-blue-900 text-white rounded-lg hover:bg-blue-800"
                  >
                    Guardar
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}